/**
 * DispatchNotesCard · v2.8+E315
 * ─────────────────────────────
 * Driver-facing, tappable/expandable card for dispatch trip notes entered in
 * the Admin "Book Now" form (stored as `dispatch_notes` on the queued ride).
 *
 *   • Renders NOTHING when there is no note (so a ride with no dispatch notes
 *     never shows an empty card).
 *   • Collapsed → compact row: "Dispatch Notes — Tap to expand" + a short
 *     one-line preview so the driver knows a note is present.
 *   • Expanded → the full note in a larger, phone-readable font.
 *
 * Uses div[role=button] (not <button>) so the larger note body can use block
 * elements without invalid HTML nesting / hydration warnings.
 */
import { useState, useRef } from "react";
import { StickyNote, ChevronDown, ChevronUp } from "lucide-react";

export default function DispatchNotesCard({ note, testId = "dispatch-notes", onFirstOpen }) {
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);
  const text = (note || "").trim();
  if (!text) return null;

  const preview = text.length > 60 ? `${text.slice(0, 60).trim()}…` : text;
  const toggle = () => {
    // Fire the read-receipt callback the first time the driver expands.
    if (!open && !firedRef.current) { firedRef.current = true; onFirstOpen?.(); }
    setOpen((o) => !o);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
      }}
      aria-expanded={open}
      data-testid={testId}
      data-open={open ? "true" : "false"}
      className="w-full cursor-pointer select-none rounded-lg border border-sky-500/50 bg-sky-500/10 px-3 py-2.5 transition-colors hover:bg-sky-500/15 focus:outline-none focus:ring-2 focus:ring-sky-400/50"
    >
      <div className="flex items-center gap-2">
        <StickyNote className="h-4 w-4 text-sky-300 flex-shrink-0" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-sky-300">
          Dispatch Notes{!open && <span className="text-sky-300/70 normal-case tracking-normal font-semibold"> — Tap to expand</span>}
        </span>
        <span className="ml-auto flex items-center text-sky-300/80">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </div>
      {open ? (
        <div
          className="mt-2 text-base leading-relaxed text-zinc-100 whitespace-pre-wrap break-words"
          data-testid={`${testId}-full`}
        >
          {text}
        </div>
      ) : (
        <div className="mt-1 text-xs text-sky-100/70 truncate" data-testid={`${testId}-preview`}>
          {preview}
        </div>
      )}
    </div>
  );
}
